/**
 * API Key Types
 *
 * Type definitions for API key management in the admin dashboard.
 */

/**
 * Client Types
 * Mirrors `API_CLIENT_TYPES` in fngr-food-be (`src/interface.ts`); keep the two in step.
 */
export const CLIENT_TYPES = ['web', 'mobile', 'cms', 'voice'] as const;

export type ClientType = (typeof CLIENT_TYPES)[number];

/**
 * What each client type is for, and how long its keys last (set by the backend).
 */
export const CLIENT_TYPE_DETAILS: Record<ClientType, { label: string; option: string }> = {
  web: { label: 'Web', option: 'Web (90-day expiry)' },
  mobile: { label: 'Mobile', option: 'Mobile (Long-term)' },
  cms: { label: 'CMS', option: 'CMS (Long-term)' },
  voice: { label: 'Voice', option: 'Voice ordering agent (Long-term)' },
};

/**
 * API Key Status Enum
 */
export type ApiKeyStatus = 'active' | 'inactive' | 'expired' | 'expiring_soon';

/**
 * API Key Interface
 * Represents a single API key in the system
 */
export interface ApiKey {
  id: string;
  keyName: string;
  clientType: ClientType;
  isActive: boolean;
  maxRequests: number;
  windowMs: number;
  createdAt: string;
  updatedAt: string;
  lastUsed: string | null;
  expiresAt: string | null;
}

/**
 * API Key List Query Parameters
 */
export interface ApiKeyListParams {
  limit?: number;
  offset?: number;
  clientType?: ClientType;
  isActive?: boolean;
}

/**
 * API Key List Response
 */
export interface ApiKeyListResponse {
  message: string;
  data: ApiKey[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

/**
 * Create API Key Request
 */
export interface CreateApiKeyRequest {
  keyName: string;
  clientType: ClientType;
  maxRequests?: number;
  windowMs?: number;
  expiresInDays?: number;
}

/**
 * Create API Key Response
 * Note: apiKey is only returned on creation
 */
export interface CreateApiKeyResponse {
  message: string;
  data: {
    id: string;
    apiKey: string;
    keyName: string;
    clientType: ClientType;
    maxRequests: number;
    windowMs: number;
    expiresAt: string | null;
    createdAt: string;
  };
}

/**
 * Update Rate Limits Request
 */
export interface UpdateRateLimitsRequest {
  maxRequests?: number;
  windowMs?: number;
}

/**
 * Rotation History Item
 */
export interface RotationHistoryItem {
  id: string;
  oldKeyId: string;
  newKeyId: string;
  rotationReason: string;
  rotatedAt: string;
  rotatedBy: string | null;
}

/**
 * Rotation Status Response
 */
export interface RotationStatusResponse {
  message: string;
  data: {
    id: string;
    keyName: string;
    clientType: ClientType;
    isActive: boolean;
    expiresAt: string | null;
    lastNotifiedAt: string | null;
    createdAt: string;
    status: ApiKeyStatus;
    daysUntilExpiry: number | null;
    wasNotified: boolean;
    autoRotationEnabled: boolean;
    rotationHistory: RotationHistoryItem[];
  };
}

/**
 * Rotate API Key Response
 * Note: newApiKey is only returned on rotation
 */
export interface RotateApiKeyResponse {
  message: string;
  data: {
    oldKeyId: string;
    newKeyId: string;
    newApiKey: string;
    newKeyName: string;
    clientType: ClientType;
    rotatedBy: string;
  };
}

/**
 * Deactivate API Key Response
 */
export interface DeactivateApiKeyResponse {
  message: string;
}

/**
 * Rotation Summary Response
 */
export interface RotationSummaryResponse {
  message: string;
  data: {
    total: number;
    web: {
      total: number;
      active: number;
      expired: number;
      expiringSoon: number;
      autoRotationEnabled: boolean;
    };
    mobile: {
      total: number;
      active: number;
      expired: number;
      expiringSoon: number;
      autoRotationEnabled: boolean;
    };
    rotations: {
      total: number;
      manual: number;
      automatic: number;
      expired: number;
    };
  };
}
